import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, ImageOff, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["Nieuw", "In Behandeling", "Ingepland", "Voltooid"];
const STATUS_COLORS = {
  Nieuw: "border-brand/50 bg-brand/10 text-brand",
  "In Behandeling": "border-safety/50 bg-safety/10 text-safety",
  Ingepland: "border-sky-400/50 bg-sky-400/10 text-sky-400",
  Voltooid: "border-green-400/50 bg-green-400/10 text-green-400",
};

const Photo = ({ path, quoteId }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl;
    axios
      .get(`${API}/files/${path}`, { withCredentials: true, responseType: "blob" })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => setUrl(false));
    return () => objectUrl && URL.revokeObjectURL(objectUrl);
  }, [path]);
  if (url === null) return <div className="flex h-40 items-center justify-center bg-ink"><Loader2 className="h-5 w-5 animate-spin text-muted2" /></div>;
  if (url === false) return <div className="flex h-40 items-center justify-center bg-ink text-muted2"><ImageOff className="h-5 w-5" /></div>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`quote-photo-${quoteId}`}>
      <img src={url} alt="Foto van aanvraag" className="h-40 w-full border-b border-white/10 object-cover transition-transform duration-300 hover:scale-[1.02]" />
    </a>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState(null);

  const load = useCallback(() => {
    axios
      .get(`${API}/quotes`, { withCredentials: true })
      .then((res) => setQuotes(res.data))
      .catch(() => setQuotes([]));
  }, []);

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
    else if (user) load();
  }, [user, navigate, load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, { withCredentials: true });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Status aangepast naar "${status}"`);
    } catch {
      toast.error("Status aanpassen mislukt");
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="h-6 w-6 animate-spin text-brand" data-testid="admin-loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <Logo height={44} />
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-xs uppercase tracking-[0.15em] text-muted2 sm:block" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-wide text-muted2 transition-colors hover:border-brand hover:text-brand"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-3.5 w-3.5" /> Afmelden
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">// Beheer</p>
            <h1 className="mt-2 font-display text-4xl font-black uppercase text-paper">Offerte-aanvragen</h1>
          </div>
          <span className="font-mono text-sm text-muted2" data-testid="quote-count">{quotes ? `${quotes.length} aanvragen` : ""}</span>
        </div>
        {quotes === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : quotes.length === 0 ? (
          <div className="mt-12 flex flex-col items-center border border-dashed border-white/15 py-20 text-center" data-testid="quotes-empty">
            <Inbox className="h-10 w-10 text-muted2" />
            <p className="mt-4 text-sm text-muted2">Nog geen aanvragen. Nieuwe offertes verschijnen hier automatisch.</p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="quotes-grid">
            {quotes.map((q) => (
              <article key={q.id} className="border border-white/10 bg-coal" data-testid={`quote-card-${q.id}`}>
                {q.photo_path && <Photo path={q.photo_path} quoteId={q.id} />}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-display text-xl font-extrabold uppercase text-paper" data-testid={`quote-name-${q.id}`}>{q.name}</h2>
                      <p className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.15em] text-brand">{q.category_label}</p>
                    </div>
                    <span className={`whitespace-nowrap border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_COLORS[q.status] || ""}`} data-testid={`quote-status-${q.id}`}>
                      {q.status}
                    </span>
                  </div>
                  <ul className="mt-4 space-y-1.5 text-sm text-muted2">
                    <li className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-brand" /><a href={`tel:${q.phone}`} className="hover:text-paper" data-testid={`quote-phone-${q.id}`}>{q.phone}</a></li>
                    <li className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-brand" /><a href={`mailto:${q.email}`} className="truncate hover:text-paper" data-testid={`quote-email-${q.id}`}>{q.email}</a></li>
                    <li className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-brand" /><span className="truncate" data-testid={`quote-address-${q.id}`}>{q.address}</span></li>
                  </ul>
                  {q.description && <p className="mt-3 border-l-2 border-brand/40 pl-3 text-sm text-muted2" data-testid={`quote-description-${q.id}`}>{q.description}</p>}
                  <div className="mt-5 flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted2">
                      {new Date(q.created_at).toLocaleString("nl-BE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
                      <SelectTrigger className="h-8 w-[150px] border-white/15 bg-ink text-xs" data-testid={`quote-status-select-${q.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`status-option-${s.replace(/\s/g, "-").toLowerCase()}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
